// Implements: F-CNV-008, F-CNV-009, F-CNV-003, F-CNV-004, F-CNV-007, F-CNV-013, F-UIX-008, F-UIX-009, F-UIX-010, F-UIX-017, F-UIX-022
/**
 * The conversation of one mockup: the thread, the live turn and the composer.
 *
 * The container owns the typed text so a refused turn leaves it in the field
 * (F-UIX-008), and it clears it only once the server has accepted the turn. The
 * composer is locked while a turn is open: either this screen started it (the
 * phase is active) or the server says somebody did (`refinement_status` is
 * `generating`). A turn that is open on the server but not started here (a reload,
 * another tab) is adopted and followed. An `interrupted` turn is not followed and
 * does not lock anything: the next message reconciles it. A turn that ended `asked` leaves its
 * questions in the thread and the focus on the composer, where the answer goes (a new turn).
 *
 * A mockup that was just created lands here with its first turn already started by the
 * creation itself (7.6, OD-7, see `firstTurnHandoff`): the screen only follows that turn (the
 * state's `turn`, then `last_turn` by id) and sends nothing. A turn that closed `failed`, `cancelled` or `interrupted` can be
 * run again (`RetryBar`, only the last one), and a message of the person can be edited in place
 * and sent again (`MessageEditor`); each refusal is shown next to the action that caused it.
 * The hand-off key leaves the history entry once the attempt
 * is settled, so a reload does not bring it back.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";

import { clearHandoff, readHandoff } from "../../../shared/api/firstTurnHandoff";
import { useRateLimitWait } from "../../../shared/api/useRateLimitWait";
import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { RateLimitWait } from "../../../shared/ui/molecules/RateLimitWait";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { useKeyShortcut } from "../../../shared/ui/useKeyShortcut";
import { isUsageReason } from "../../../shared/ui/molecules/usageRefusal";
import type { ConversationMessage, TurnMode, TurnStatusName } from "../../../types/api";
import { isActivePhase } from "../../../types/generation";
import { BRIEF_FIELD_ID, BriefEditor } from "../components/BriefEditor";
import { EDITOR_FIELD_ID, MessageEditor } from "../components/MessageEditor";
import { MessageThread, editButtonId } from "../components/MessageThread";
import { RetryBar } from "../components/RetryBar";
import { readReferenceImage } from "../../../shared/api/referenceImage";
import { COMPOSER_FIELD_ID, TurnComposer } from "../components/TurnComposer";
import type { ImageRefusal } from "../components/TurnComposer";
import { CANCEL_SHORTCUT, STATUS_REGION_ID, TurnProgress, isCancellable } from "../components/TurnProgress";
import { GENERATING, useConversationState, usePollWhile } from "../hooks/useConversationState";
import { useMessages } from "../hooks/useMessages";
import { readBrief, writeBrief } from "../model/briefSections";
import { DEFAULT_STREAM_RETRY, useTurn } from "../hooks/useTurn";
import type { StreamRetry } from "../hooks/useTurn";
import styles from "./ConversationContainer.module.css";

/** Which action the turn's last refusal belongs to. */
type Intent = "composer" | "retry" | "edit" | "brief";

/** Where the focus goes once what held it is gone. */
type FocusNext = "status" | { readonly messageId: string };

/** The message being edited and the words typed so far. */
interface Editing {
  readonly messageId: string;
  readonly text: string;
}

/** The reference image attached to the next message: its name and what is sent for it. */
interface AttachedFile {
  readonly name: string;
  readonly base64: string;
  readonly digest: string;
}

/** The statuses of a closed turn that can be run again (D-P5-19). */
const RETRYABLE: readonly TurnStatusName[] = ["failed", "cancelled", "interrupted"];

/** What happened in the conversation, for a page that hides it (owner round 3: the collapsed rail). */
export interface ConversationActivity {
  readonly turnOpen: boolean;
  readonly newestMessageId: string | null;
}

export interface ConversationContainerProps {
  readonly mockupId: string;
  /** How the stream is opened while the run is not recorded yet; tests make it fast. */
  readonly streamRetry?: StreamRetry | undefined;
  /**
   * What the page shows above the composer, told whether a turn is open now (UIX.1: the 80 %
   * notice, which reads the usage status again when a turn settles). A slot, because the
   * usage module is another module and a container imports none (F-UIX-009).
   */
  readonly statusNotice?: ((turnOpen: boolean) => ReactNode) | undefined;
  /** The page's own controls in the column's head (UX-05: focus mode), after the title. */
  readonly headActions?: ReactNode;
  /** Told whether a turn is open and which message is the newest, each time either changes. */
  readonly onActivity?: ((activity: ConversationActivity) => void) | undefined;
}

export function ConversationContainer({
  mockupId,
  streamRetry = DEFAULT_STREAM_RETRY,
  statusNotice,
  headActions,
  onActivity,
}: ConversationContainerProps): ReactElement {
  const { t } = useTranslation();
  const { push } = useToast();
  const thread = useMessages(mockupId);
  const conversation = useConversationState(mockupId);
  const turn = useTurn(mockupId, streamRetry);
  const location = useLocation();
  const navigate = useNavigate();
  const [handoff] = useState(() => readHandoff(location.state, mockupId));
  const [text, setText] = useState("");
  const { adopt } = turn;
  // Taken on arrival, one tick later: a screen left (or mounted twice in a row, as React
  // does in development) before that tick follows nothing. Following is free: nothing is sent.
  useEffect(() => {
    if (handoff === null) return undefined;
    const timer = setTimeout(() => {
      adopt(handoff.turnId, handoff.generationId);
      // Read once: the entry keeps its address and every other key, and loses only this one.
      void navigate(`${location.pathname}${location.search}${location.hash}`, {
        replace: true,
        state: clearHandoff(location.state),
      });
    }, 0);
    return () => {
      clearTimeout(timer);
    };
    // Only the first render's hand-off matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const open = conversation !== undefined && !conversation.interrupted ? conversation.turn : null;
  const adoptableTurn = open?.generation_id != null ? open.id : null;
  const adoptableRun = open?.generation_id ?? null;
  const adoptableStatus = open?.status;
  const phaseNow = turn.phase;

  // Also when this screen gives up on a turn (`interrupted`) and the server later shows it
  // somewhere else (dispatched, say): `adopt` follows it again only then.
  useEffect(() => {
    if (adoptableTurn !== null && adoptableRun !== null) {
      adopt(adoptableTurn, adoptableRun, adoptableStatus);
    }
  }, [adoptableTurn, adoptableRun, adoptableStatus, phaseNow, adopt]);

  const generating = conversation?.refinement_status === GENERATING;
  const locked = isActivePhase(turn.phase) || generating;
  const newestMessageId = thread.status === "success" ? (thread.messages.at(-1)?.id ?? null) : null;
  const report = useRef(onActivity);
  useEffect(() => {
    report.current = onActivity;
  }, [onActivity]);
  useEffect(() => {
    report.current?.({ turnOpen: locked, newestMessageId });
  }, [locked, newestMessageId]);
  // UIX.2: a `429` to a send is a wait, not a failure. Nothing can be sent until it ends, and
  // nothing is resent by itself: the person presses again (checklist 13).
  const waitSeconds = useRateLimitWait(turn.error);
  const sendLocked = locked || waitSeconds !== null;
  const refusal = turn.error?.status === 429 ? null : turn.error;
  // Locked by a turn this screen does not follow: ask again until the server says it is over.
  usePollWhile(mockupId, generating && !isActivePhase(turn.phase), streamRetry);

  // A turn that ended asking questions: the answer is next, so the composer takes the focus
  // once (and only once per such turn) it is free to type in.
  const asked = turn.phase === "asked";
  const focused = useRef(false);
  useEffect(() => {
    if (!asked) {
      focused.current = false;
      return;
    }
    if (locked || focused.current) return;
    focused.current = true;
    document.getElementById(COMPOSER_FIELD_ID)?.focus();
  }, [asked, locked]);

  // Whose refusal the turn's error is: the composer's, the retry's. Each shows its own.
  const [intent, setIntent] = useState<Intent>("composer");

  const { start } = turn;
  // F-CNV-010: the choice exists only over a version. It belongs to this container (keyed by
  // mockup id by the page), survives a refusal and goes back to modifying once a send is accepted.
  const [mode, setMode] = useState<TurnMode>("modify");
  const hasVersion = conversation?.current_version_id != null;
  // F-CNV-016 (OD-8): opt-in, unchecked by default, and offered only when the turn draws from
  // scratch (no version yet, or regenerate): a modification has no brief (the server says 422).
  const [reviewBrief, setReviewBrief] = useState(false);
  const draws = !hasVersion || mode === "regenerate";
  // F-CNV-012 (OD-11): one optional reference image. Like the text it survives a refusal and goes
  // only once the server has accepted the turn. The checks here are a quick answer; the server judges.
  const [attached, setAttached] = useState<AttachedFile | null>(null);
  const [imageRefusal, setImageRefusal] = useState<ImageRefusal | null>(null);
  // Which choice is the latest: a file still being read when another is chosen (or when this one
  // is removed) is ignored by its number, not by luck of timing.
  const choice = useRef(0);
  const selectImage = useCallback((file: File): void => {
    choice.current += 1;
    const mine = choice.current;
    setImageRefusal(null);
    void readReferenceImage(file).then((read) => {
      if (choice.current !== mine) return;
      if (read.kind === "ok") {
        setAttached({ name: file.name, base64: read.base64, digest: read.digest });
      } else {
        setImageRefusal(read.reason);
      }
    });
  }, []);
  const removeImage = useCallback((): void => {
    choice.current += 1;
    setAttached(null);
    setImageRefusal(null);
  }, []);
  const submit = useCallback((): void => {
    setIntent("composer");
    void start(
      text.trim(),
      hasVersion && mode === "regenerate" ? "regenerate" : undefined,
      draws && reviewBrief,
      attached === null ? undefined : { base64: attached.base64, digest: attached.digest },
    ).then((accepted) => {
      if (accepted) {
        setText("");
        setMode("modify");
        setReviewBrief(false);
        choice.current += 1;
        setAttached(null);
        setImageRefusal(null);
      }
    });
  }, [text, start, hasVersion, mode, draws, reviewBrief, attached]);

  // Only the LAST closed turn, and only when it did not finish and nothing is open.
  const lastTurn = conversation?.last_turn ?? null;
  // UIX.2: a turn a usage limit closed (a race refused it in the job) names the reason in its
  // error message, the newest of the thread. Trying again changes nothing: it is not offered.
  const newest = thread.status === "success" ? thread.messages.at(-1) : undefined;
  const closedByUsage = newest?.kind === "error" && isUsageReason(newest.error_code);
  const retryable =
    !locked &&
    conversation?.turn === null &&
    lastTurn !== null &&
    RETRYABLE.includes(lastTurn.status) &&
    !closedByUsage;
  const retryTargetId = lastTurn?.id ?? null;
  // The turn ended asking questions: besides answering, the person can ask to generate with the
  // best assumptions, so the questions never block. Sent through the same path as a typed message.
  const askingQuestions = newest?.kind === "question" && !sendLocked;
  const generateWithoutAnswering = useCallback((): void => {
    setIntent("composer");
    void start(t("conversation.questions.canned"));
  }, [start, t]);
  // F-CNV-011: the turn on screen is the open one, else the last closed one. Its own flag only,
  // so a note never carries over from another turn (the element is keyed by that turn's id).
  const shownTurn = conversation?.turn ?? lastTurn;
  // F-CNV-016: the brief of the last turn, when it closed `briefed` and nothing is open. The
  // draft belongs to that turn id, so a newer brief is never shown with an older one's edits.
  const briefTurnId = lastTurn?.status === "briefed" && lastTurn.brief != null ? lastTurn.id : null;
  const reviewing = !locked && conversation?.turn === null && briefTurnId !== null;
  // F-UIX-022: the brief is read into its sections once per text; the edits are the values of
  // those sections, kept under the turn id they were typed for. A brief of another turn (a newer
  // review, a late answer) shows its own words, never the edits typed for an older one.
  const proposedBrief = briefTurnId === null ? "" : (lastTurn?.brief ?? "");
  const briefParts = useMemo(() => readBrief(proposedBrief), [proposedBrief]);
  const [briefDraft, setBriefDraft] = useState<{
    readonly turnId: string;
    readonly values: readonly string[];
  } | null>(null);
  const briefValues =
    briefDraft?.turnId === briefTurnId ? briefDraft.values : briefParts.map((part) => part.value);
  const briefText = writeBrief(briefParts, briefValues);
  // The screen followed this turn to its end: the brief is what comes next, so it takes the focus once.
  const briefFocused = useRef<string | null>(null);
  useEffect(() => {
    if (!reviewing || turn.phase !== "briefed" || briefFocused.current === briefTurnId) return;
    briefFocused.current = briefTurnId;
    document.getElementById(BRIEF_FIELD_ID)?.focus();
  }, [reviewing, turn.phase, briefTurnId]);
  // One editor at a time, for the message last chosen; the typed words survive a refusal.
  const [editing, setEditing] = useState<Editing | null>(null);
  const editingId = editing?.messageId ?? null;
  useEffect(() => {
    if (editingId !== null) document.getElementById(EDITOR_FIELD_ID)?.focus();
  }, [editingId]);
  // Where the focus goes next, once the thing that held it is gone (a closed editor, a button
  // that disappeared because a turn opened): the status of the turn, or one message's edit button.
  const focusNext = useRef<FocusNext | null>(null);
  const [, render] = useState(0);
  const setFocusNext = useCallback((next: FocusNext): void => {
    focusNext.current = next;
    render((count) => count + 1);
  }, []);
  // After every render: the thing to focus may only exist once the screen has caught up.
  useEffect(() => {
    const next = focusNext.current;
    if (next === null) return;
    const target =
      next === "status"
        ? document.getElementById(STATUS_REGION_ID)
        : document.getElementById(editButtonId(next.messageId));
    if (target === null) return;
    target.focus();
    focusNext.current = null;
  });
  const { dismissError } = turn;
  const startEditing = useCallback((message: ConversationMessage): void => {
    dismissError();
    setIntent("edit");
    setEditing({ messageId: message.id, text: message.body });
  }, [dismissError]);
  const { edit } = turn;
  const submitEdit = useCallback((): void => {
    if (editing === null) return;
    setIntent("edit");
    void edit(editing.messageId, editing.text.trim()).then((accepted) => {
      if (accepted) {
        setEditing(null);
        setFocusNext("status");
      }
    });
  }, [editing, edit, setFocusNext]);

  const { confirm } = turn;
  const confirmBrief = useCallback((): void => {
    if (briefTurnId === null) return;
    setIntent("brief");
    void confirm(briefTurnId, briefText.trim()).then((accepted) => {
      if (accepted) {
        setBriefDraft(null);
        setFocusNext("status");
      }
    });
  }, [confirm, briefTurnId, briefText, setFocusNext]);

  const { retryLast } = turn;
  const retry = useCallback((): void => {
    if (retryTargetId === null) return;
    setIntent("retry");
    void retryLast(retryTargetId).then((accepted) => {
      if (accepted) setFocusNext("status");
    });
  }, [retryLast, retryTargetId, setFocusNext]);

  const { cancel: cancelTurn } = turn;
  const cancel = useCallback((): void => {
    void cancelTurn().then((outcome) => {
      if (outcome === "refused") push({ tone: "danger", message: t("conversation.cancel.failed") });
    });
  }, [cancelTurn, push, t]);
  // Escape does what the cancel button does, under the button's own rule (F-UIX-017), and only
  // from this conversation or the bare page: an Escape meant for anything else stays its own.
  const region = useRef<HTMLElement>(null);
  const canCancel = turn.generationId !== null;
  useKeyShortcut(CANCEL_SHORTCUT, isCancellable(turn.phase, canCancel), cancel, region);

  return (
    <section ref={region} className={styles.conversation} aria-labelledby="conversation-title">
      <div className={styles.head}>
        <h2 id="conversation-title" className={styles.title}>
          {t("conversation.title")}
        </h2>
        <span className={styles.spacer} />
        {headActions}
      </div>
      {/* The log scrolls on its own: in the tab order so the keyboard can scroll it (WCAG 2.1.1). */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scrolling region must be reachable */}
      <div className={styles.thread} role="log" aria-label={t("conversation.log.label")} tabIndex={0}>

        {thread.status === "pending" ? <LoadingState label={t("common.state.loading")} /> : null}
        {thread.status === "error" ? (
          <ErrorState error={thread.error} onRetry={thread.refetch} />
        ) : null}
        {thread.status === "success" && thread.messages.length === 0 ? (
          <EmptyState icon="message" headingLevel={3} title={t("conversation.emptyTitle")} body={t("conversation.empty")} />
        ) : null}
        {thread.status === "success" && thread.messages.length > 0 ? (
          <MessageThread
            mockupId={mockupId}
            messages={thread.messages}
            loadingMore={thread.loadingMore}
            onEdit={locked ? undefined : startEditing}
            editor={
              editing === null
                ? undefined
                : {
                    messageId: editing.messageId,
                    node: (
                      <MessageEditor
                        key={editing.messageId}
                        text={editing.text}
                        onTextChange={(value) => {
                          setEditing({ messageId: editing.messageId, text: value });
                          turn.dismissError();
                        }}
                        onSubmit={submitEdit}
                        onCancel={() => {
                          setEditing(null);
                          setFocusNext({ messageId: editing.messageId });
                          turn.dismissError();
                        }}
                        disabled={sendLocked}
                        error={intent === "edit" ? refusal : null}
                      />
                    ),
                  }
            }
          />
        ) : null}
        {thread.status === "success" && thread.moreError !== null ? (
          <ErrorState error={thread.moreError} onRetry={thread.retryMore} />
        ) : null}
        {thread.status === "success" && thread.loadingMore ? (
          <p className={styles.notice} role="status">
            {t("conversation.loadingMore")}
          </p>
        ) : null}

        {conversation?.interrupted === true ? (
          <p className={styles.notice} role="status">
            {t("conversation.interrupted")}
          </p>
        ) : null}

        {turn.phase !== "idle" ? (
          <TurnProgress
            phase={turn.phase}
            canCancel={canCancel}
            onCancel={cancel}
            progress={turn.progress}
          />
        ) : null}

        {shownTurn?.context_trimmed === true ? (
          <p key={shownTurn.id} className={styles.notice} role="status">
            {t("conversation.contextTrimmed")}
          </p>
        ) : null}

        {retryable && waitSeconds === null ? (
          <RetryBar onRetry={retry} error={intent === "retry" ? refusal : null} />
        ) : null}

        {reviewing ? (
          <BriefEditor
            key={briefTurnId}
            parts={briefParts}
            values={briefValues}
            onValueChange={(index, value) => {
              setBriefDraft({ turnId: briefTurnId, values: briefValues.with(index, value) });
              turn.dismissError();
            }}
            onSubmit={confirmBrief}
            disabled={sendLocked}
            error={intent === "brief" ? refusal : null}
          />
        ) : null}
      </div>

      <div className={styles.foot}>
        {waitSeconds === null ? null : <RateLimitWait seconds={waitSeconds} />}

        {statusNotice?.(locked)}

        {askingQuestions ? (
          <div className={styles.skip}>
            <Button variant="secondary" size="sm" onClick={generateWithoutAnswering}>
              {t("conversation.questions.generate")}
            </Button>
          </div>
        ) : null}

        <TurnComposer
          text={text}
          onTextChange={(value) => {
            setText(value);
            turn.dismissError();
          }}
          onSubmit={submit}
          disabled={sendLocked}
          waiting={waitSeconds !== null}
          error={intent === "composer" ? refusal : null}
          mode={hasVersion ? { value: mode, onChange: setMode } : undefined}
          reviewBrief={draws ? { checked: reviewBrief, onChange: setReviewBrief } : undefined}
          image={{
            name: attached?.name ?? null,
            refusal: imageRefusal,
            onSelect: selectImage,
            onRemove: removeImage,
          }}
        />
      </div>
    </section>
  );
}
